import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import ReactTooltip from "react-tooltip";
import { useAuthContext } from "../../../../../components/Auth";
import { DropDown } from "../../../../../components/DropDown/DropDown";
import { SecondaryButtonMediumV2 } from "../../../../../components/Buttons/Buttons";
import { SoftHeader2 } from "../../../../../components/Typography/Typography";
import type { DataMutate } from "../../../../../types/types";
import type { PIMProduct } from "../../../../../types/types.PIM";
import { useRoutePath } from "../../../../../util/Routing";
import type { MinimalApiResponse } from "../ProductsList";
import {
  getCompareDisableReason,
  MAX_COMPARE_PRODUCTS,
} from "../CompareProducts";
import { AccessUpdate } from "./AccessUpdate";
import { ProductStatusUpdate } from "./ProductStatusUpdate";
import { StorefrontConnectionUpdate } from "./StorefrontConnectionUpdate";

const BulkActionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  .place_above {
    z-index: 3;
  }
`;

const CompareButtonWrap = styled.span`
  display: inline-flex;
`;

export type BulkProductsType = {
  id: string;
  name: string;
  pim_product_type?: PIMProduct["pim_product_type"];
  /** Present when available from the current products table page. */
  template_id?: string | null;
};

export const BulkActionOnProducts = ({
  rows,
  mutateProducts,
  onResetSelectedRows,
}: {
  rows: BulkProductsType[] | Promise<BulkProductsType[]>;
  mutateProducts: DataMutate<MinimalApiResponse>;
  onResetSelectedRows: () => void;
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { adminPath } = useRoutePath();
  const { hasPermission } = useAuthContext();
  const canBulkEdit = hasPermission("modify_products");

  const [selectedAction, setSelectedAction] = useState<string>();
  const [selected_rows, set_selected_rows] = useState<BulkProductsType[]>([]);
  const [isResolvingSelectAll, setIsResolvingSelectAll] = useState(false);

  const bulkActions = useMemo(
    () => [
      t("Change Status"),
      t("Activate/Deactivate on storefront"),
      t("Manage access"),
    ],
    [t]
  );

  const selectBulkAction = (action: string) => {
    setSelectedAction(action);
  };

  const resetBulkAction = () => setSelectedAction(undefined);

  const onCompleteAction = () => {
    resetBulkAction();
    mutateProducts();
    onResetSelectedRows();
  };

  // Preserve demo behavior: resolve Promise ("select all") into selected_rows.
  useEffect(() => {
    let cancelled = false;
    if (rows instanceof Promise) {
      setIsResolvingSelectAll(true);
      rows.then((data) => {
        if (!cancelled) {
          set_selected_rows(data);
          setIsResolvingSelectAll(false);
        }
      });
    } else {
      setIsResolvingSelectAll(false);
      set_selected_rows(rows);
    }
    return () => {
      cancelled = true;
    };
  }, [rows]);

  const product_ids = selected_rows.map((p) => p.id);

  // While select-all is still resolving, treat as over the compare limit so the
  // button stays disabled (select-all is almost always > 4 products).
  const selectedCount = isResolvingSelectAll
    ? MAX_COMPARE_PRODUCTS + 1
    : selected_rows.length;
  const templateIds = selected_rows.map((row) => row.template_id ?? null);
  const disableReason = getCompareDisableReason(
    selectedCount,
    templateIds,
    t
  );
  const compareEnabled = !disableReason;

  const handleCompare = () => {
    if (!compareEnabled) return;
    const productNumbers = product_ids.slice(0, MAX_COMPARE_PRODUCTS);
    const params = new URLSearchParams();
    params.set("products", productNumbers.join(","));
    history.push(`${adminPath}/pim/compare-products?${params.toString()}`);
  };

  return (
    <BulkActionWrapper>
      {canBulkEdit && (
        <>
          <SoftHeader2>{t("Bulk Actions:")}</SoftHeader2>
          <DropDown
            items={bulkActions}
            activeItem={selectedAction ?? ""}
            showIcon
            hideBorder={false}
            textLeft={t("Manage") + ":"}
            direction={"left"}
            clickHandler={selectBulkAction}
            className="place_above"
          />
        </>
      )}
      {selected_rows.length > 0 && (
        <CompareButtonWrap
          data-tip={disableReason || ""}
          data-for="compare-products-tooltip"
        >
          <SecondaryButtonMediumV2
            onClick={handleCompare}
            disabled={!compareEnabled}
          >
            {t("Compare Products")}
          </SecondaryButtonMediumV2>
          {disableReason && (
            <ReactTooltip id="compare-products-tooltip" effect="solid" />
          )}
        </CompareButtonWrap>
      )}
      {canBulkEdit && (
        <>
          <ProductStatusUpdate
            show={selectedAction === bulkActions[0]}
            onCompleteUpdate={onCompleteAction}
            resetBulkAction={resetBulkAction}
            product_ids={product_ids}
            onCancelUpdate={resetBulkAction}
          />
          <StorefrontConnectionUpdate
            show={selectedAction === bulkActions[1]}
            onComplete={onCompleteAction}
            onCancel={resetBulkAction}
            product_ids={product_ids}
          />
          <AccessUpdate
            show={selectedAction === bulkActions[2]}
            product_ids={product_ids}
            onComplete={onCompleteAction}
            onCancel={resetBulkAction}
          />
        </>
      )}
    </BulkActionWrapper>
  );
};
